import { getBasename, getDirname } from '../platformAbstraction/pathComposition';
import { isValidGUID } from '../util/general';

/**
 * To be on the safe side in case we would ever need to use yet
 * another file naming strategy to organize items in subfolders,
 * we will place all files using subfolders inside a subfolder.
 *
 * For instance a file named "d50b58fd-d033-46ed-92f8-dcf290135db6.mxf"
 * that should go inside "/kelda/kelda-root/primary-root"
 * would be stored in "/kelda/kelda-root/primary-root/v1/d5/0b/d50b58fd-d033-46ed-92f8-dcf290135db6.mxf"
 */
const ITEM_ID_SUBFOLDERS_ROOT = 'v1';

export interface LegacyFile {
  isLegacyFilePath: true;
  newPath: string;
}

export interface NotLegacyFile {
  isLegacyFilePath: false;
}

export type LegacyFileStatus = LegacyFile | NotLegacyFile;


/**
 * Given a file basename, returns the relative subfolder to use.
 * For instance, a file named "d50b58fd-d033-46ed-92f8-dcf290135db6.mxf"
 * would have the relative subfolder "v1/d5/0b"
 */
export function getItemIdRelativeSubfolder(fileBaseName: string, pathSep: string): string {
  return `${ITEM_ID_SUBFOLDERS_ROOT}${pathSep}${fileBaseName.substring(0, 2)}${pathSep}${fileBaseName.substring(2, 4)}`;
}


/**
 * Returns true if the given file path is a legacy file path,
 * i.e. it refers to a direct child of the primary root folder
 * and the file name starts with an item ID.
 */
function isLegacyFilePath(rootFolder: string, filePath: string, pathSep: string): boolean {
  const prefix = `${rootFolder}${pathSep}`;
  if (!filePath.startsWith(prefix)) {
    // The file is not in the primary root folder
    return false;
  }

  const basename = filePath.substring(prefix.length);
  if (basename.includes(pathSep)) {
    // The file is not a direct child
    return false;
  }

  return isValidGUID(basename.substring(0, 36));
}


function getItemIdPathWithSubfolders(legacyFilePath: string, pathSep: string): string {
  const rootFolder = getDirname(legacyFilePath, pathSep);
  const basename = getBasename(legacyFilePath, pathSep);
  const relativeSubfolder = getItemIdRelativeSubfolder(basename, pathSep);
  return `${rootFolder}${pathSep}${relativeSubfolder}${pathSep}${basename}`;
}


export function getLegacyFileStatus(rootFolder: string, filePath: string, pathSep: string): LegacyFileStatus {
  if (!isLegacyFilePath(rootFolder, filePath, pathSep)) {
    return {
      isLegacyFilePath: false,
    };
  }

  return {
    isLegacyFilePath: true,
    newPath: getItemIdPathWithSubfolders(filePath, pathSep),
  };
}
